// @flow
import React, { useEffect, useState, useRef } from 'react';
import { connect } from 'react-redux';
import { CSVLink } from 'react-csv';
import debounce from 'lodash.debounce';
import {
  Card,
  Table,
  Row,
  Col,
  Tooltip,
  Badge,
} from 'antd';
import {
  SpButton,
  FilterWrapper,
  SpText,
} from 'components/DesignKit';
import {
  axiosAuthInstance,
  API_END_POINTS,
} from 'api';
import styled from 'styled-components';
import getPaymentLinkBackground from 'utils/getPaymentLinkBackground';
import getPaymentlinkStatus from 'utils/getPaymentlinkStatus';
import getUniquePaymentTypes from 'utils/getUniquePaymentTypes';

import Loading from 'components/Loading';
import Filters from 'components/Filters';
import getCountryPhoneCode from 'utils/getCountryPhoneCode';
import getFilterOperators from 'utils/getFilterOperators';

import {
  paymentsLinkAction,
  notificationActions,
  invoiceDetailsAction,
  subscriptionAction,
  filterParamsActions,
} from 'store/actions';
import { NOTIFICATION, FILTERS_SPECS } from 'store/actionTypes';
import {
  PAGES,
  ROLES,
  MESSAGES,
  CURRENCY,
  FILTERS_TYPES,
} from 'appconstants';
import queryString from 'query-string';
import { useLocation } from 'react-router-dom';
import moment from 'moment-timezone';
import formatNumber from 'utils/formatNumber';
import getSortedList from 'utils/getSortedList';
import RBAC from 'components/rbac';
import FilterIcon from 'components/NavIcons/filterIcon';
import ExportIcon from 'components/NavIcons/exportIcon';
import Clipboard from 'components/NavIcons/resendIcon';
import AddPaymentLink from './components/AddPaymentLink';

const MOMENT_FORMAT = 'YYYY-MM-DDTHH:mm:ss';

type Props = {
  paymentsLink: Array<Object>,
  fetchPaymentsLink: Function,
  addPaymentsLink: Function,
  setNotification: Function,
  selectedAccount: Object,
  totalElements: number,
  test: boolean,
  loading: boolean,
  submitting: boolean,
  validator: boolean,
  history: {
    push: Function,
  },
  getCustomersList: Function,
  fetchProfile: Function,
  customersList: Array<Object>,
  profile: Array<Object>,
  setFilterData: Function,
  preserveFilters: Object,
};

// $FlowFixMe
const StatusTag = styled('div')({
  borderRadius: '10px',
  display: 'inline-block',
  paddingLeft: '5px',
  paddingRight: '5px',
  marginLeft: 'auto',
  marginRight: 'auto',
  minWidth: '75px',
  textAlign: 'center',
});

const PaymentsLinkList = (props: Props) => {
  const location = useLocation();
  const {
    selectedAccount,
    paymentsLink,
    setNotification,
    loading,
    addPaymentsLink,
    fetchPaymentsLink,
    totalElements,
    submitting,
    validator,
    test,
    history,
    getCustomersList,
    fetchProfile,
    customersList,
    profile,
    preserveFilters,
    setFilterData,
  } = props;

  const [pagination, setPagination] = useState({});
  const [addPaymentLinkModal, setAddPaymentLinkModal] = useState(false);
  const [phoneCode, setPhoneCode] = useState('');
  const id = selectedAccount && selectedAccount.currency && selectedAccount.currency.id;
  const { role } = profile;
  const { pathname } = location;
  const myRef = useRef();
  const [open, setOpen] = useState(false);
  const PAYMENTLINKS_FILTERS_TYPES = (role && role.name === ROLES.PARTNER) ? FILTERS_TYPES.PARTNER_PAYMENTLINKS : FILTERS_TYPES.PAYMENTLINKS;
  const [filterTypes, setFilterTypes] = useState((role && role.name === ROLES.PARTNER)
    ? FILTERS_TYPES.PARTNER_PAYMENTLINKS
    : FILTERS_TYPES.PAYMENTLINKS);
  const [fiterOperators, setFilterOperators] = useState([]);
  const [filterPayments, setFilterPayments] = useState((preserveFilters && preserveFilters.filterPayments)
    ? (preserveFilters && preserveFilters.filterPayments)
    : [{
      filterType: '',
      filterName: '',
      filterOperator: '',
      filterValue: '',
      filterNameDisplay: '',
      filterValueFrom: '',
      filterValueTo: '',
      checked: true,
    }]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [currentDate, setCurrentDate] = useState('');
  const [count, setCount] = useState(null);
  const [fillColor, setFillColor] = useState(false);
  const [filterColor, setFilterColor] = useState(false);
  const [clearFilters, setClearFilters] = useState(false);
  const [nameSortOrder, setNameSortOrder] = useState(null);
  const [emailSortOrder, setEmailSortOrder] = useState(null);
  const [phoneSortOrder, setPhoneSortOrder] = useState(null);
  const [amountSortOrder, setAmountSortOrder] = useState(null);
  const [defaultSortOrder, setDefaultSortOrder] = useState('descend');
  const currencyType = selectedAccount && selectedAccount.currency;
  const paymentMethods = getUniquePaymentTypes(selectedAccount && selectedAccount.supportedPaymentTypes);
  const supportedCurrency = (selectedAccount && selectedAccount.supportedCurrencies);
  const preserveParam = ((preserveFilters && preserveFilters.pathname) === pathname)
    ? queryString.parse(preserveFilters.search)
    : setFilterData({
      pathname,
      search: '',
      filterPayments: [{
        filterType: '',
        filterName: '',
        filterOperator: '',
        filterValue: '',
        filterNameDisplay: '',
        filterValueFrom: '',
        filterValueTo: '',
        checked: true,
      }],
    });

  const exportData = paymentsLink ? paymentsLink.map((values) => ({
    name: values.name,
    email: values.email,
    phoneNumber: `=""${values.phoneNumber}""`,
    amount: `${values.currency.prefix}${formatNumber((values.amount / 100).toFixed(values.currency.toFixed), currencyType)}`,
    status: getPaymentlinkStatus(values.status),
    createdAt: moment
      .utc(values.createdAt)
      .tz(selectedAccount.timezone)
      .format('MMM DD, YYYY, hh:mm a'),
  })) : '';

  const exportHeaders = [
    { label: 'NAME', key: 'name' },
    { label: 'EMAIL', key: 'email' },
    { label: 'PHONE', key: 'phoneNumber' },
    { label: 'AMOUNT', key: 'amount' },
    { label: 'STATUS', key: 'status' },
    { label: 'CREATED ON', key: 'createdAt' },
  ];

  const debouncedFetchPaymentsLink = debounce(fetchPaymentsLink, 2000, { leading: true });

  const getData = (query) => {
    const parsed = queryString.parse(query);
    const currentPage = parsed.pageNum || 1;
    const sortBy = parsed.sortBy || 'createdAt';
    const direction = parsed.direction || 'DESC';
    const sortParams = `&sortBy=${sortBy}&direction=${direction}`;
    const filterParam = parsed.filterParams || '';
    const filterParams = (preserveParam ? preserveParam.filterParams : filterParam || '');
    debouncedFetchPaymentsLink({ currentPage, sortParams, filterParams });
    if (!clearFilters) {
      getCustomersList();
      fetchProfile();
    }
  };

  useEffect(() => {
    getData(location.search);
    if (selectedAccount && selectedAccount.country) {
      setPhoneCode(getCountryPhoneCode(selectedAccount.country.id));
    }
  }, [location, test, selectedAccount]);

  const handleClick = e => {
    if (myRef.current && myRef.current.contains(e.target)) {
      return;
    }
    if (e.target.value === undefined
      && e.toElement.className !== 'ant-calendar-date'
      && e.toElement.className !== 'ant-calendar-prev-month-btn'
      && e.toElement.className !== 'ant-calendar-next-month-btn') {
      setOpen(false);
    }
  };

  useEffect(() => {
    if (!validator) {
      setAddPaymentLinkModal(false);
    }
  }, [validator]);

  useEffect(() => {
    const parsed = queryString.parse(location.search);
    const currentPage = parsed.pageNum || 1;
    setPagination(existingElements => ({
      ...existingElements,
      total: totalElements,
      current: parseInt(currentPage, 10),
      showTotal: total => `Total ${total} items`,
    }));
  }, [paymentsLink]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClick);
    return () => {
      document.removeEventListener('mousedown', handleClick);
    };
  }, []);

  const getCustomers = (searchText) => {
    getCustomersList(searchText);
  };

  const addNewPaymentLink = (payment: Object) => {
    const {
      name,
      email,
      phoneNumber,
      countryCode,
      amount,
      notify,
      description,
      redirectUrl,
      paymentMethodData,
      expiryDate,
      customerGid,
      currencyCode,
      applicationFee,
      canDecline,
      serviceFee,
      serviceFeeEnabled,
    } = payment;
    const currency = amount.replace(/,/g, '');
    const payloadApplicationFee = applicationFee ? applicationFee.replace(/[^\d]/g, '') : '';
    const payloadserviceFee = serviceFee ? serviceFee.replace(/[^\d]/g, '') : '';
    const data = {
      amount: Number((currency * 100).toFixed(0)),
      notificationType: notify,
      redirectUri: redirectUrl,
      description,
      canDecline,
      currencyCode: currencyCode || CURRENCY(id),
      paymentMethodType: paymentMethodData,
      expiresAt: expiryDate,
      applicationFee: payloadApplicationFee ? Number(payloadApplicationFee) : null,
      serviceFee: payloadserviceFee ? Number(payloadserviceFee) : null,
      serviceFeeEnabled,
    };
    if (customerGid) {
      data.customerGid = customerGid;
    } else {
      data.customer = {
        email,
        name,
        phoneNumber: phoneNumber ? `${countryCode}${phoneNumber}` : null,
        taxId: 'NONE',
        taxStatus: 'exempt',
        taxValue: null,
      };
    }
    addPaymentsLink({ ...data });
  };

  const handleTableChange = (page, sorter, filters) => {
    const parsed = queryString.parse(location.search);
    const filterParams = parsed.filterParams || '';
    const tempPage = page.current;
    let tempParams = '';
    if (filters.order === 'ascend') {
      tempParams = `&sortBy=${filters.field}&direction=ASC`;
    } else if (filters.order === 'descend') {
      tempParams = `&sortBy=${filters.field}&direction=DESC`;
    } else {
      tempParams = '';
    }
    if (location.search && filterParams) {
      const filterParam = encodeURIComponent(filterParams);
      history.push({
        pathname: PAGES.PAYMENTLINK,
        search: `?filterParams=${filterParam}&pageNum=${tempPage}${tempParams}`,
      });
    } else {
      history.push({
        pathname: PAGES.PAYMENTLINK,
        search: `?pageNum=${tempPage}${tempParams}`,
      });
    }
  };

  const selectedRecord = (record) => {
    const paymentLinkGid = record.gid;
    history.push({
      pathname: `${PAGES.PAYMENTLINK}/details/${paymentLinkGid}`,
      state: location.search,
    });
  };

  // const copyCode = (record) => {
  //   const el = record.link;
  //   navigator.clipboard.writeText(el);
  //   document.execCommand('copy');
  //   setNotification({
  //     type: NOTIFICATION.SUCCESS,
  //     payload: MESSAGES.PAYMENTSLINK.SUCCESS,
  //   });
  // };

  const notifyPaymentLink = async (record) => {
    const pymntlinkgid = record?.gid;
    try {
      await axiosAuthInstance.post(`${API_END_POINTS.PAYMENTLINK}/${pymntlinkgid}/notify`);
      setNotification({
        type: NOTIFICATION.SUCCESS,
        payload: MESSAGES.NOTIFY.SUCCESS,
      });
    } catch (e) {
      setNotification({
        type: NOTIFICATION.ERROR,
        payload: e.response.data && e.response.data.message,
      });
    }
  };

  const onCheck = (e, index) => {
    const list = [...filterPayments];
    if (filterPayments.length !== 1) {
      if (!e) {
        list.splice(index, 1);
        const filterTypeOptions = PAYMENTLINKS_FILTERS_TYPES.filter(({ value: id1 }) => !list.some(({ filterName: id2 }) => id2 === id1));
        setFilterTypes(filterTypeOptions);
      } else {
        list[index].checked = e;
      }
    }
    setFilterPayments(list);
  };

  const handleChange = (e, index) => {
    const list = [...filterPayments];
    if (e[1] === 'createdAt') {
      list[index].filterType = 'Date';
    } else if (e[1] === 'amount') {
      list[index].filterType = 'Number';
    } else if (e[1] === 'applicationFee') {
      list[index].filterType = 'Number';
    } else if (e[1] === 'status') {
      list[index].filterType = 'Dropdown';
    } else {
      list[index].filterType = 'String';
    }
    list[index].filterName = e[1];
    list[index].filterNameDisplay = e[0];
    list[index].filterOperator = '';
    list[index].filterValue = '';
    const filterTypeOptions = PAYMENTLINKS_FILTERS_TYPES.filter(({ value: id1 }) => !list.some(({ filterName: id2 }) => id2 === id1));
    setFilterPayments(list);
    setFilterTypes(filterTypeOptions);
    setFilterOperators(getFilterOperators(e[1]));
  };

  const handleFromValue = (e, index) => {
    const list = [...filterPayments];
    const regex = /^\d*\.?\d*$/;
    if (e.currentTarget && regex.test(e.currentTarget.value.replace(/,/g, ''))) {
      list[index].filterValueFrom = e.currentTarget.value;
      setFilterPayments(list);
    } else if (regex.test(e.replace(/,/g, ''))) {
      list[index].filterValueFrom = e;
      setFilterPayments(list);
    }
  };

  const handleToValue = (e, index) => {
    const list = [...filterPayments];
    const regex = /^\d*\.?\d*$/;
    if (e.currentTarget && regex.test(e.currentTarget.value.replace(/,/g, ''))) {
      list[index].filterValueTo = e.currentTarget.value;
      setFilterPayments(list);
    } else if (regex.test(e.replace(/,/g, ''))) {
      list[index].filterValueTo = e;
      setFilterPayments(list);
    }
  };

  const handleValue = (e, index) => {
    let selectedValue = '';
    if (filterPayments[index].filterName === 'createdAt') {
      if (filterPayments[index].filterOperator === 'BT') {
        const date1 = e[0];
        const date2 = e[1];
        const utcDate1 = moment(date1).endOf('day');
        setStartDate(utcDate1);
        const utcDate2 = moment(date2).startOf('day');
        setEndDate(utcDate2);
        const utcString1 = utcDate1.format(MOMENT_FORMAT);
        const utcString2 = utcDate2.format(MOMENT_FORMAT);
        const selectedDate1 = moment.tz(utcString1, selectedAccount.timezone);
        const selectedDate2 = moment.tz(utcString2, selectedAccount.timezone);
        const dateStart = selectedDate1.utc().format(MOMENT_FORMAT);
        const dateEnd = selectedDate2.utc().format(MOMENT_FORMAT);
        selectedValue = `createdAt.GT=${dateStart}&createdAt.LT=${dateEnd}`;
      } else if (filterPayments[index].filterOperator === 'LT') {
        const utcDate = moment(e).startOf('day');
        setCurrentDate(utcDate);
        const utcString = utcDate.format(MOMENT_FORMAT);
        const dateSelected = moment.tz(utcString, selectedAccount.timezone);
        const dateStart = dateSelected.utc().format(MOMENT_FORMAT);
        selectedValue = `createdAt.LT=${dateStart}`;
      } else if (filterPayments[index].filterOperator === 'GT') {
        const utcDate = moment(e).endOf('day');
        setCurrentDate(utcDate);
        const utcString = utcDate.format(MOMENT_FORMAT);
        const dateSelected = moment.tz(utcString, selectedAccount.timezone);
        const dateStart = dateSelected.utc().format(MOMENT_FORMAT);
        selectedValue = `createdAt.GT=${dateStart}`;
      }
    } else if (filterPayments[index].filterName === 'status') {
      selectedValue = e;
    } else {
      if (!e.currentTarget) {
        selectedValue = e;
      }
      if (e.currentTarget && e.currentTarget.value) {
        selectedValue = e.currentTarget.value;
      }
    }
    const list = [...filterPayments];
    list[index].filterValue = selectedValue;
    setFilterPayments(list);
  };

  const handleOperator = (e, index) => {
    const list = [...filterPayments];
    list[index].filterOperator = e;
    if (list[index].filterValue) {
      if (list[index].filterOperator !== 'BT'
        && list[index].filterName === 'createdAt') {
        handleValue(currentDate, index);
      } else if (list[index].filterOperator === 'BT'
        && list[index].filterName === 'createdAt') {
        const dateList = [startDate, endDate];
        handleValue(dateList, index);
      } else if (list[index].filterOperator !== 'BT'
        && list[index].filterOperator !== 'LT'
        && list[index].filterOperator !== 'GT') {
        const value = filterPayments[index].filterValue;
        handleValue(value, index);
      }
    }
    setFilterPayments(list);
  };

  const handleAddClick = () => {
    const list = [...filterPayments];
    setFilterPayments([...list, {
      filterType: '',
      filterName: '',
      filterOperator: '',
      filterValue: '',
      filterNameDisplay: '',
      filterValueFrom: '',
      filterValueTo: '',
      checked: true,
    }]);
  };

  const resetFilters = () => {
    setFilterData({
      pathname,
      search: '',
    });
    setClearFilters(true);
    setFilterPayments([{
      filterType: '',
      filterName: '',
      filterOperator: '',
      filterValue: '',
      filterNameDisplay: '',
      filterValueFrom: '',
      filterValueTo: '',
      checked: true,
    }]);
    if (profile && profile.role && profile.role.name === ROLES.PARTNER) {
      setFilterTypes(FILTERS_TYPES.PARTNER_PAYMENTLINKS);
    } else {
      setFilterTypes(FILTERS_TYPES.PAYMENTLINKS);
    }
    setStartDate('');
    setEndDate('');
    setCurrentDate('');
    setCount(null);
    history.push(PAGES.PAYMENTLINK);
  };

  const applyFilters = () => {
    const paymentsFilters = filterPayments.filter((item) => item.checked);
    for (let i = 0; i < paymentsFilters.length; i += 1) {
      if (!paymentsFilters[i].filterName || !paymentsFilters[i].filterOperator || !paymentsFilters[i].filterValue) {
        return;
      }
    }
    if (paymentsFilters[0].filterValue || (paymentsFilters[0].filterValueFrom && paymentsFilters[0].filterValueTo)) {
      setCount(paymentsFilters && paymentsFilters.length);
      for (let i = 0; i < paymentsFilters.length; i += 1) {
        if (paymentsFilters[i].checked) {
          if (paymentsFilters[i].filterName === 'createdAt') {
            paymentsFilters[i] = paymentsFilters[i].filterValue;
          } else if (paymentsFilters[i].filterName === 'amount' && paymentsFilters[i].filterOperator === 'BT') {
            const name = paymentsFilters[i].filterName;
            const from = paymentsFilters[i].filterValueFrom.replace(/[. ,:-]+/g, '');
            const to = paymentsFilters[i].filterValueTo.replace(/[. ,:-]+/g, '');
            paymentsFilters[i] = `${name}.GT=${from}&${name}.LT=${to}`;
          } else if (paymentsFilters[i].filterName === 'amount' && paymentsFilters[i].filterOperator !== 'BT') {
            const value = paymentsFilters[i].filterValue.replace(/[. ,:-]+/g, '');
            paymentsFilters[i] = `${paymentsFilters[i].filterName}.${paymentsFilters[i].filterOperator}=${value}`;
          } else if (paymentsFilters[i].filterName === 'phoneNumber') {
            const value = paymentsFilters[i].filterValue.replace('+', '%2B');
            paymentsFilters[i] = `${paymentsFilters[i].filterName}.${paymentsFilters[i].filterOperator}=${value}`;
          } else if (paymentsFilters[i].filterName === 'applicationFee' && paymentsFilters[i].filterOperator === 'BT') {
            const name = paymentsFilters[i].filterName;
            const from = paymentsFilters[i].filterValueFrom.replace(/[. ,:-]+/g, '');
            const to = paymentsFilters[i].filterValueTo.replace(/[. ,:-]+/g, '');
            paymentsFilters[i] = `${name}.GT=${from}&${name}.LT=${to}`;
          } else if (paymentsFilters[i].filterName === 'applicationFee' && paymentsFilters[i].filterOperator !== 'BT') {
            const value = paymentsFilters[i].filterValue.replace(/[. ,:-]+/g, '');
            paymentsFilters[i] = `${paymentsFilters[i].filterName}.${paymentsFilters[i].filterOperator}=${value}`;
          } else if (paymentsFilters[i].filterName === 'email') {
            const email = encodeURIComponent(paymentsFilters[i].filterValue);
            paymentsFilters[i] = `${paymentsFilters[i].filterName}.${paymentsFilters[i].filterOperator}=${email}`;
          } else {
            paymentsFilters[i] = `${paymentsFilters[i].filterName}.${paymentsFilters[i].filterOperator}=${paymentsFilters[i].filterValue}`;
          }
        }
      }

      const filterParams = encodeURIComponent(paymentsFilters.join('&'));
      const parsed = queryString.parse(location.search);
      const currentPage = 1;
      const sortBy = parsed.sortBy || 'createdAt';
      const direction = parsed.direction || 'DESC';
      const sortParams = `&sortBy=${sortBy}&direction=${direction}`;
      if (location.search && parsed.sortBy && parsed.pageNum && parsed.direction) {
        setFilterData({
          pathname,
          search: `?filterParams=${filterParams}&pageNum=${currentPage}${sortParams}`,
          filterPayments,
        });
        history.push({
          pathname: PAGES.PAYMENTLINK,
          search: `?filterParams=${filterParams}&pageNum=${currentPage}${sortParams}`,
        });
      } else {
        setFilterData({
          pathname,
          search: `?filterParams=${filterParams}`,
          filterPayments,
        });
        history.push({
          pathname: PAGES.PAYMENTLINK,
          search: `?filterParams=${filterParams}`,
        });
      }
      setOpen(false);
    }
  };

  const partnerColumns = [
    {
      title: 'NAME',
      dataIndex: 'name',
      width: '11%',
      align: 'left',
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
      sortOrder: nameSortOrder,
      onHeaderCell: () => ({
        onClick: () => {
          const newSortOrder = nameSortOrder === 'ascend' ? 'descend' : nameSortOrder === 'descend' ? 'ascend' : 'ascend';
          setNameSortOrder(newSortOrder);
          setEmailSortOrder(null);
          setPhoneSortOrder(null);
          setDefaultSortOrder(null);
          setAmountSortOrder(null);
        },
      }),
      render: (name) => (
        <Tooltip placement="top" title="Click here to view details">
          <span style={{ cursor: 'pointer' }}>
            {name || <>&#8211;</>}
          </span>
        </Tooltip>
      ),
    },
    {
      title: 'EMAIL',
      dataIndex: 'email',
      width: '12%',
      align: 'left',
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
      sortOrder: emailSortOrder,
      onHeaderCell: () => ({
        onClick: () => {
          const newSortOrder = emailSortOrder === 'ascend' ? 'descend' : emailSortOrder === 'descend' ? 'ascend' : 'ascend';
          setNameSortOrder(null);
          setEmailSortOrder(newSortOrder);
          setPhoneSortOrder(null);
          setDefaultSortOrder(null);
          setAmountSortOrder(null);
        },
      }),
      render: (email) => (
        <Tooltip placement="top" title="Click here to view details">
          <span style={{ cursor: 'pointer' }}>
            {email || <>&#8211;</>}
          </span>
        </Tooltip>
      ),
    },
    {
      title: 'PHONE',
      dataIndex: 'phoneNumber',
      width: '12%',
      align: 'left',
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
      sortOrder: phoneSortOrder,
      onHeaderCell: () => ({
        onClick: () => {
          const newSortOrder = phoneSortOrder === 'ascend' ? 'descend' : phoneSortOrder === 'descend' ? 'ascend' : 'ascend';
          setNameSortOrder(null);
          setEmailSortOrder(null);
          setDefaultSortOrder(null);
          setPhoneSortOrder(newSortOrder);
          setAmountSortOrder(null);
        },
      }),
      render: (phoneNumber) => (
        <Tooltip placement="top" title="Click here to view details">
          <span style={{ cursor: 'pointer' }}>
            {phoneNumber || <>&#8211;</>}
          </span>
        </Tooltip>
      ),
    },
    {
      title: 'AMOUNT',
      dataIndex: 'amount',
      width: '14%',
      align: 'left',
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
      sortOrder: amountSortOrder,
      onHeaderCell: () => ({
        onClick: () => {
          const newSortOrder = amountSortOrder === 'ascend' ? 'descend' : amountSortOrder === 'descend' ? 'ascend' : 'ascend';
          setNameSortOrder(null);
          setEmailSortOrder(null);
          setPhoneSortOrder(null);
          setDefaultSortOrder(null);
          setAmountSortOrder(newSortOrder);
        },
      }),
      render: (amount, record) => {
        const total = (amount / 100).toFixed(record.currency.toFixed);
        return (
          <span> {`${record.currency.prefix} ${formatNumber(total, currencyType)}`}</span>
        );
      },
    },
    {
      title: 'APPLICATION FEE',
      dataIndex: 'applicationFee',
      width: '16%',
      align: 'left',
      sorter: false,
      sortDirections: ['ascend', 'descend', 'ascend'],
      render: (applicationFee, record) => {
        const total = (applicationFee / 100).toFixed(2);
        return (
          <span> {`${record.currency.prefix} ${formatNumber(total, currencyType)}`}</span>
        );
      },
    },
    {
      title: 'STATUS',
      dataIndex: 'status',
      width: '12%',
      align: 'left',
      sorter: false,
      sortDirections: ['ascend', 'descend', 'ascend'],
      render: (status) => (
        <Tooltip placement="top" title="Click here to view details">
          <StatusTag style={{ backgroundColor: getPaymentLinkBackground(status) }}>
            {getPaymentlinkStatus(status)}
          </StatusTag>
        </Tooltip>
      ),
    },
    {
      title: 'CREATED ON',
      dataIndex: 'createdAt',
      width: '14%',
      align: 'left',
      sorter: true,
      defaultSortOrder: 'descend',
      sortDirections: ['ascend', 'descend', 'ascend'],
      sortOrder: defaultSortOrder,
      onHeaderCell: () => ({
        onClick: () => {
          const newSortOrder = defaultSortOrder === 'ascend' ? 'descend' : defaultSortOrder === 'descend' ? 'ascend' : 'descend';
          setDefaultSortOrder(newSortOrder);
          setNameSortOrder(null);
          setEmailSortOrder(null);
          setPhoneSortOrder(null);
          setAmountSortOrder(null);
        },
      }),
      render: (createdAt) => {
        if (createdAt && selectedAccount && selectedAccount.timezone) {
          const tzDate = moment
            .utc(createdAt)
            .tz(selectedAccount.timezone)
            .format('MMM DD, YYYY, hh:mm a');
          return (<span>{tzDate}</span>);
        }
        return (
          <>&#8211;</>
        );
      },
    },
  ];


  const columns = [
    {
      title: 'NAME',
      dataIndex: 'name',
      width: '13%',
      align: 'left',
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
      sortOrder: nameSortOrder,
      onHeaderCell: () => ({
        onClick: () => {
          const newSortOrder = nameSortOrder === 'ascend' ? 'descend' : nameSortOrder === 'descend' ? 'ascend' : 'ascend';
          setNameSortOrder(newSortOrder);
          setEmailSortOrder(null);
          setPhoneSortOrder(null);
          setDefaultSortOrder(null);
          setAmountSortOrder(null);
        },
      }),
      render: (name) => (
        <Tooltip placement="top" title="Click here to view details">
          <span style={{ cursor: 'pointer' }}>
            {name || <>&#8211;</>}
          </span>
        </Tooltip>
      ),
    },
    {
      title: 'EMAIL',
      dataIndex: 'email',
      width: '15%',
      align: 'left',
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
      sortOrder: emailSortOrder,
      onHeaderCell: () => ({
        onClick: () => {
          const newSortOrder = emailSortOrder === 'ascend' ? 'descend' : emailSortOrder === 'descend' ? 'ascend' : 'ascend';
          setNameSortOrder(null);
          setEmailSortOrder(newSortOrder);
          setPhoneSortOrder(null);
          setDefaultSortOrder(null);
          setAmountSortOrder(null);
        },
      }),
      render: (email) => (
        <Tooltip placement="top" title="Click here to view details">
          <span style={{ cursor: 'pointer' }}>
            {email || <>&#8211;</>}
          </span>
        </Tooltip>
      ),
    },
    {
      title: 'PHONE',
      dataIndex: 'phoneNumber',
      width: '13%',
      align: 'left',
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
      sortOrder: phoneSortOrder,
      onHeaderCell: () => ({
        onClick: () => {
          const newSortOrder = phoneSortOrder === 'ascend' ? 'descend' : phoneSortOrder === 'descend' ? 'ascend' : 'ascend';
          setNameSortOrder(null);
          setEmailSortOrder(null);
          setDefaultSortOrder(null);
          setPhoneSortOrder(newSortOrder);
          setAmountSortOrder(null);
        },
      }),
      render: (phoneNumber) => (
        <Tooltip placement="top" title="Click here to view details">
          <span style={{ cursor: 'pointer' }}>
            {phoneNumber || <>&#8211;</>}
          </span>
        </Tooltip>
      ),
    },
    {
      title: 'AMOUNT',
      dataIndex: 'amount',
      width: '13%',
      align: 'left',
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
      sortOrder: amountSortOrder,
      onHeaderCell: () => ({
        onClick: () => {
          const newSortOrder = amountSortOrder === 'ascend' ? 'descend' : amountSortOrder === 'descend' ? 'ascend' : 'ascend';
          setNameSortOrder(null);
          setEmailSortOrder(null);
          setPhoneSortOrder(null);
          setDefaultSortOrder(null);
          setAmountSortOrder(newSortOrder);
        },
      }),
      render: (amount, record) => {
        const total = (amount / 100).toFixed(record.currency.toFixed);
        return (
          <span> {`${record.currency.prefix} ${formatNumber(total, currencyType)}`}</span>
        );
      },
    },
    {
      title: 'STATUS',
      dataIndex: 'status',
      width: '15%',
      align: 'left',
      sorter: false,
      sortDirections: ['ascend', 'descend', 'ascend'],
      render: (status) => (
        <Tooltip placement="top" title="Click here to view details">
          <StatusTag style={{ backgroundColor: getPaymentLinkBackground(status) }}>
            {getPaymentlinkStatus(status)}
          </StatusTag>
        </Tooltip>
      ),
    },
    {
      title: 'CREATED ON',
      dataIndex: 'createdAt',
      width: '20%',
      align: 'left',
      sorter: true,
      defaultSortOrder: 'descend',
      sortDirections: ['ascend', 'descend', 'ascend'],
      sortOrder: defaultSortOrder,
      onHeaderCell: () => ({
        onClick: () => {
          const newSortOrder = defaultSortOrder === 'ascend' ? 'descend' : defaultSortOrder === 'descend' ? 'ascend' : 'descend';
          setDefaultSortOrder(newSortOrder);
          setNameSortOrder(null);
          setEmailSortOrder(null);
          setPhoneSortOrder(null);
          setAmountSortOrder(null);
        },
      }),
      render: (createdAt) => {
        if (createdAt && selectedAccount && selectedAccount.timezone) {
          const tzDate = moment
            .utc(createdAt)
            .tz(selectedAccount.timezone)
            .format('MMM DD, YYYY, hh:mm a');
          return (<span>{tzDate}</span>);
        }
        return (
          <>&#8211;</>
        );
      },
    },
  ];

  const actionColumn = {
    title: 'ACTION',
    dataIndex: 'action',
    width: '15%',
    align: 'center',
    render: (text, record) => {
      const { status } = record;
      if (status === 'ACTIVE') {
        return (
          <span>
            <SpButton
              type="secondary"
              borderRadius="50px"
              onClick={(e) => { e.stopPropagation(); notifyPaymentLink(record); }}
            >
              <Clipboard />
            </SpButton>
          </span>
        );
      }
      return (
        <span>
          <SpButton
            type="secondary"
            borderRadius="50px"
            disabled
          >
            <Clipboard />
          </SpButton>
        </span>
      );
    },
  };

  if (loading || !selectedAccount) {
    return <Loading />;
  }

  return (
    <Card>
      <Row className="my-2">
        <div className="d-flex justify-content-end">
          <RBAC
            permit={[ROLES.OWNER, ROLES.ADMINISTRATOR, ROLES.DEVELOPER, ROLES.OPERATIONS, ROLES.SUPERUSER, ROLES.OFFICEMANAGER, ROLES.OFFICESUPPORT]}
          >
            <Col>
              <SpButton
                type="secondary"
                shape="round"
                ghost
                className="mr-3"
                onClick={() => setAddPaymentLinkModal(true)}
              >
                {'\u002B'}&nbsp;Add Link
              </SpButton>
              <span>
                {
                  exportData !== null
                    ? (
                      <Tooltip placement="left" title="Click here to export Payment Links">
                        <CSVLink
                          target="_blank"
                          className="mr-3"
                          filename="Payments Link Report.csv"
                          data={exportData}
                          headers={exportHeaders}
                          onMouseEnter={() => setFillColor(true)}
                          onMouseLeave={() => setFillColor(false)}
                        >
                          <ExportIcon
                            fill={fillColor
                              ? '#279dfe'
                              : '#C0C0C0'}
                          />
                        </CSVLink>
                      </Tooltip>
                    ) : null
                }
              </span>
              <span ref={myRef}>
                <span
                  onClick={() => setOpen(!open)}
                  style={{ cursor: 'pointer' }}
                  onMouseEnter={() => setFilterColor(true)}
                  onMouseLeave={() => setFilterColor(false)}
                >
                  {open && (
                    <Badge count={count} style={{ backgroundColor: '#0090ff' }}>
                      <FilterIcon
                        fill={filterColor
                          ? '#279dfe'
                          : '#C0C0C0'}
                      />
                    </Badge>
                  )}
                  {!open && (
                    <Tooltip placement="left" title="Click here to filter Payment Links">
                      <Badge count={count} style={{ backgroundColor: '#0090ff' }}>
                        <FilterIcon
                          fill={filterColor
                            ? '#279dfe'
                            : '#C0C0C0'}
                        />
                      </Badge>
                    </Tooltip>
                  )}
                </span>
                {open && (
                  <FilterWrapper
                    style={{ marginLeft: '-80px' }}
                  >
                    <Filters
                      filtersGrid={filterPayments}
                      filterTypes={filterTypes}
                      handleChange={handleChange}
                      onCheck={onCheck}
                      fiterOperators={fiterOperators}
                      handleOperator={handleOperator}
                      handleValue={handleValue}
                      startDate={startDate}
                      endDate={endDate}
                      currentDate={currentDate}
                      handleFromValue={handleFromValue}
                      handleToValue={handleToValue}
                    />
                    <div className="px-4 mt-2">
                      {
                        filterPayments.length < PAYMENTLINKS_FILTERS_TYPES.length && (
                          <div className="mb-2 ml-3" style={{ cursor: 'pointer' }}>
                            <SpText
                              fontWeight="600"
                              color="#279dfe"
                              onClick={handleAddClick}
                            >
                              +&nbsp; Add another filter
                            </SpText>
                          </div>
                        )
                      }
                    </div>
                    <div className="d-flex mb-2 justify-content-end mr-3">
                      <SpButton
                        type="secondary"
                        shape="round"
                        className="mr-2"
                        ghost
                        onClick={resetFilters}
                      >
                        Clear
                      </SpButton>
                      <SpButton
                        type="primary"
                        shape="round"
                        onClick={applyFilters}
                      >
                        Save
                      </SpButton>
                    </div>
                  </FilterWrapper>
                )}
              </span>
            </Col>
          </RBAC>
        </div>
      </Row>

      {/* ADD PAYMENTLINK MODAL */}
      {addPaymentLinkModal && (
        <AddPaymentLink
          visible={addPaymentLinkModal}
          submitting={submitting}
          phoneCode={phoneCode}
          id={id}
          selectedAccount={selectedAccount}
          close={() => setAddPaymentLinkModal(false)}
          submit={addNewPaymentLink}
          role={profile && profile.role}
          customersList={getSortedList(customersList)}
          paymentMethods={paymentMethods}
          supportedCurrency={supportedCurrency}
          getCustomers={getCustomers}
        />
      )}
      <RBAC
        fallback={(
          <Table
            onRow={(record) => ({
              onClick: () => {
                selectedRecord(record);
              },
            })}
            columns={columns}
            rowKey={(record) => record.gid}
            dataSource={paymentsLink}
            pagination={pagination}
            onChange={handleTableChange}
          />
        )}
        permit={[ROLES.OWNER, ROLES.ADMINISTRATOR, ROLES.DEVELOPER, ROLES.OPERATIONS, ROLES.SUPERUSER, ROLES.OFFICEMANAGER, ROLES.OFFICESUPPORT]}
      >
        <Table
          onRow={(record) => ({
            onClick: () => {
              selectedRecord(record);
            },
          })}
          rowClassName={() => 'ant-table-clickable-row'}
          columns={(role && role.name === ROLES.PARTNER) ? [...partnerColumns, actionColumn] : [...columns, actionColumn]}
          rowKey={(record) => record.gid}
          dataSource={paymentsLink}
          pagination={pagination}
          onChange={handleTableChange}
        />
      </RBAC>
    </Card>
  );
};

const mapStateToProps = (state) => ({
  paymentsLink: state.paymentsLink.content,
  profile: state.subscription.profile,
  totalElements: state.paymentsLink.totalElements,
  loading: state.loading.loading,
  validator: state.loading.validator,
  submitting: state.loading.submitting,
  test: state.account.test,
  selectedAccount: state.account.selectedAccount,
  isSuperUser: state.user.superUser,
  customersList: state.invoiceDetails.customersList,
  preserveFilters: state.filterParams.preserveFilters,
});

const mapDispatchToProps = (dispatch) => ({
  fetchPaymentsLink: param => dispatch(paymentsLinkAction.fetchPaymentsLink({
    payload: param,
  })),
  addPaymentsLink: param => dispatch(paymentsLinkAction.addPaymentsLink({
    payload: param,
  })),
  setNotification: ({ type, payload }) => dispatch(notificationActions.setNotification({
    type,
    payload,
  })),
  fetchProfile: param => dispatch(subscriptionAction.fetchProfile({
    payload: param,
  })),
  getCustomersList: param => dispatch(invoiceDetailsAction.getCustomersList({
    payload: param,
  })),
  setFilterData: (value) => dispatch(filterParamsActions.setFilterData({
    type: FILTERS_SPECS.SET_FILTER_DATA,
    payload: value,
  })),
});

// $FlowFixMe
export default connect(mapStateToProps, mapDispatchToProps)(PaymentsLinkList);
